<template>
  <v-layout ma-0 fill-height>
    <component :is="enforceRatio ? 'v-responsive' : 'v-layout'" :aspect-ratio="aspectRatio" ma-0>
      <v-layout column ma-0>
        <v-img :aspect-ratio="aspectRatio" :src="thumbnailUrl" :contain="true">
          <v-layout ma-0 column fill-height class="lightbox white--text">
            <v-spacer></v-spacer>
            <v-flex shrink class="text-xs-right" v-if="!hideDefaultActionButton">
              <v-btn color="primary" @click="dialog = !dialog">{{ $t('media.crop.cta1') }}</v-btn>
            </v-flex>
            <slot name="overimage"></slot>
          </v-layout>
        </v-img>
        <slot name="underimage" :on="() => dialog = !dialog"></slot>
      </v-layout>
    </component>
    <v-dialog v-model="dialog" :persistent="true" v-if="dialog">
      <v-card>
        <v-card-text class="pa-1">
          <cropper
            ref="cropper"
            class="cropper"
            :src="imageUrl"
            :stencil-props="{
              aspectRatio
            }"
            @change="change"
            :min-height="400"
            :min-width="400"
            :default-position="defaultPosition"
            :default-size="defaultSize"
          ></cropper>
        </v-card-text>
        <v-card-actions class="pt-2 pb-3 text-xs-center justify-center">
          <v-btn @click="cancel">{{ $t('media.crop.annulla') }}</v-btn>
          <v-btn color="primary" @click="confirm">{{ $t('media.crop.conferma') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { VResponsive } from 'vuetify/lib'
import { $cloudinaryUrl } from '@/mixins/cloudinary'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css';

export default {
  props: {
    aspectRatio: {
      type: Number,
      required: true
    },
    value: {
      type: Object,
      default: () => ({ top: 0, left: 0, width: 0, height: 0 })
    },
    id: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'image'
    },
    version: {
      type: Number
    },
    transformation: {
      type: String,
      required: true
    },
    hideDefaultActionButton: {
      type: Boolean,
      default: false
    },
    enforceRatio: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: null,
      backupValue: this.value
    }
  },
  mixins: [$cloudinaryUrl],
  components: { Cropper, VResponsive },
  watch: {
    dialog(newValue) {
      const { cropper } = this.$refs;
      if (newValue) {
        // We need to call cropper refresh after dialog appears to let it resize correctly
        // https://github.com/Norserium/vue-advanced-cropper/issues/182
        cropper.refresh();
      }
    }
  },
  computed: {
    imageUrl() {
      return this.$cloudinaryUrl(this.type, this.id, 'q_auto,f_jpg', this.version) // type, id, transformation, version)
    },
    thumbnailUrl() {
      return this.$cloudinaryUrl(this.type, this.id, this.currentTransformation, this.version) // type, id, transformation, version)
    },
    currentTransformation() {
      let coordinates = { ...this.value };
      // If dialog is open, don't rely on new values to avoid multiple networks calls
      if (this.dialog) {
        coordinates = { ...this.backupValue };
      }
      return (coordinates.width != 0 || coordinates.height != 0) ? `q_auto,f_jpg,c_crop,h_${coordinates.height},w_${coordinates.width},x_${coordinates.left},y_${coordinates.top}/c_scale,h_300` : this.transformation
    }
  },
  methods: {
    cancel() {
      this.$emit('input', this.backupValue)
      this.dialog = false;
    },
    confirm() {
      this.backupValue = this.value;
      this.dialog = false;
    },
    change({ coordinates }) {
      this.$emit('input', { ...coordinates, width: Math.round(coordinates.width), height: Math.round(coordinates.height), left: Math.round(coordinates.left), top: Math.round(coordinates.top) })
    },
    defaultPosition({ visibleArea, coordinates, imageSize }) {
      return {
        left: this.value.left || (imageSize.width / 2 - coordinates.width / 2),
        top: this.value.top || (imageSize.height / 2 - coordinates.height / 2),
      };
    },
    defaultSize({ imageSize, visibleArea }) {
      return {
        width: this.value.width || (visibleArea || imageSize).width,
        height: this.value.height || (visibleArea || imageSize).height,
      };
    }
  }
}
</script>

<style scoped>
.cropper {
  height: 70vh;
  background: #000;
}
.text-center {
  text-align: center;
}
.pointer {
  cursor: pointer;
}
</style>